'use client';

import { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2, RefreshCw, AlertCircle, CheckCircle, XCircle, Wifi, Key } from 'lucide-react';
import { useToast } from '@/hooks/useToast';

interface MCPTool {
  name: string;
  description?: string;
  inputSchema?: any;
}

interface MCPServer {
  id: string;
  name: string;
  url: string;
  status: 'active' | 'inactive' | 'error';
  lastError?: string;
  authType?: 'none' | 'bearer' | 'custom' | 'oauth2';
  authConfig?: {
    accessToken?: string;
    accessTokenRef?: string;
    // other fields not needed for UI
  };
  discoveredTools?: {
    tools: MCPTool[];
    lastDiscovered: string;
  };
}

interface MCPServersListProps {
  servers: MCPServer[];
  availableTools: any[];
  selectedTools: string[];
  onToolToggle: (toolId: string, enabled: boolean) => void;
  onBatchToolToggle?: (toolIds: string[], enable: boolean) => void;
  onServerDeleted: (serverId: string) => void;
  onRefresh?: () => Promise<void>;
  agentId?: string;
  source?: 'chat-admin' | 'widget-admin';
}

export function MCPServersList({ 
  servers, 
  availableTools,
  selectedTools, 
  onToolToggle, 
  onBatchToolToggle,
  onServerDeleted,
  onRefresh,
  agentId,
  source = 'widget-admin'
}: MCPServersListProps) {
  const [expandedServers, setExpandedServers] = useState<Set<string>>(new Set());
  const [loadingServers, setLoadingServers] = useState<Set<string>>(new Set());
  const { showToast } = useToast();

  const toggleServerExpanded = (serverId: string) => {
    const newExpanded = new Set(expandedServers);
    if (newExpanded.has(serverId)) {
      newExpanded.delete(serverId);
    } else {
      newExpanded.add(serverId);
    }
    setExpandedServers(newExpanded);
  };

  const handleRefreshTools = async (server: MCPServer) => {
    setLoadingServers(new Set(loadingServers).add(server.id));
    
    try {
      const response = await fetch(`/api/agents/${agentId}/mcp-servers/${server.id}/discover`, {
        method: 'POST',
      });

      if (response.ok) {
        showToast('Tools refreshed successfully', 'success');
        // Call the parent's refresh function to reload tools without leaving the page
        if (onRefresh) {
          await onRefresh();
        }
        // Expand this server to show the refreshed tools
        const newExpanded = new Set(expandedServers);
        newExpanded.add(server.id);
        setExpandedServers(newExpanded);
      } else {
        const error = await response.json();
        showToast(error.error || 'Failed to refresh tools', 'error');
      }
    } catch (error) {
      console.error('Failed to refresh tools:', error);
      showToast('Failed to refresh tools', 'error');
    } finally {
      const newLoading = new Set(loadingServers);
      newLoading.delete(server.id);
      setLoadingServers(newLoading);
    }
  };

  const handleDeleteServer = async (server: MCPServer) => {
    if (!confirm(`Are you sure you want to delete "${server.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/agents/${agentId}/mcp-servers/${server.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        showToast('Server deleted successfully', 'success');
        onServerDeleted(server.id);
      } else {
        const error = await response.json();
        showToast(error.error || 'Failed to delete server', 'error');
      }
    } catch (error) {
      console.error('Failed to delete server:', error);
      showToast('Failed to delete server', 'error');
    }
  };

  const handleTestConnection = async (server: MCPServer) => {
    setLoadingServers(new Set(loadingServers).add(server.id));
    
    try {
      const response = await fetch(`/api/agents/${agentId}/mcp-servers/${server.id}/test`, {
        method: 'POST',
      });

      const result = await response.json();
      
      if (result.success) {
        showToast('Connection successful', 'success');
      } else {
        showToast(result.message || 'Connection failed', 'error');
      }
      
      // Reload to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Failed to test connection:', error);
      showToast('Failed to test connection', 'error');
    } finally {
      const newLoading = new Set(loadingServers);
      newLoading.delete(server.id);
      setLoadingServers(newLoading);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
  };

  if (servers.length === 0) {
    return (
      <div className="px-6 py-8 text-center text-gray-500">
        No MCP servers configured
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {servers.map((server) => {
        const isExpanded = expandedServers.has(server.id);
        const isLoading = loadingServers.has(server.id);
        const toolCount = server.discoveredTools?.tools.length || 0;
        
        // Get tools for this server from availableTools
        const serverTools = availableTools.filter(tool => 
          tool.mcpServerId === server.id
        );

        return (
          <div key={server.id}>
            {/* Server Header */}
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => toggleServerExpanded(server.id)}
                  className="flex items-center flex-1 text-left"
                >
                  <div className="flex items-center">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-400 mr-2" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400 mr-2" />
                    )}
                    {getStatusIcon(server.status)}
                  </div>
                  <div className="ml-3 flex-1">
                    <h4 className="text-sm font-medium text-gray-900">{server.name}</h4>
                    <p className="text-sm text-gray-500">{server.url}</p>
                    {server.status === 'error' && server.lastError ? (
                      <p className="text-xs text-red-600 mt-1">
                        Error: {server.lastError}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-400 mt-1">
                        {toolCount} {toolCount === 1 ? 'tool' : 'tools'} discovered
                      </p>
                    )}
                  </div>
                </button>
                <div className="flex items-center space-x-2">
                  {/* Re-authenticate button for OAuth2 servers (always available, not just on error) */}
                  {server.authType === 'oauth2' && (
                    <button
                      onClick={async () => {
                        try {
                          const response = await fetch(`/api/agents/${agentId}/mcp-servers/${server.id}/oauth2/initiate`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                              source,
                              forceReauth: true, // Always force re-auth when user clicks the button
                            }),
                          });
                          if (response.ok) {
                            const data = await response.json();
                            if (data.authorizationUrl) {
                              window.location.href = data.authorizationUrl;
                            } else if (data.status === 'authenticated') {
                              showToast('Already authenticated. Try refreshing tools.', 'info');
                            } else {
                              showToast('No authorization URL returned', 'error');
                            }
                          } else {
                            const errorData = await response.json().catch(() => ({}));
                            showToast(errorData.error || 'Failed to initiate OAuth 2.1 flow', 'error');
                          }
                        } catch (error) {
                          showToast('Failed to initiate OAuth 2.1 flow', 'error');
                        }
                      }}
                      disabled={isLoading}
                      className={`group relative p-2 disabled:opacity-50 ${
                        server.status === 'error'
                          ? 'text-red-500 hover:text-red-700'
                          : 'text-gray-400 hover:text-indigo-600'
                      }`}
                      title={server.authConfig?.accessToken || server.authConfig?.accessTokenRef
                        ? "Re-authenticate OAuth 2.1"
                        : "Authorize with OAuth 2.1"}
                    >
                      <Key className={`h-4 w-4 ${isLoading ? 'animate-pulse' : ''}`} />
                      <span className="absolute right-0 top-8 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                        {server.authConfig?.accessToken || server.authConfig?.accessTokenRef
                          ? "Re-authenticate"
                          : "Authorize OAuth 2.1"}
                      </span>
                    </button>
                  )}
                  {/* Test connection button for non-OAuth2 servers with errors */}
                  {server.status === 'error' && server.authType !== 'oauth2' && (
                    <button
                      onClick={() => handleTestConnection(server)}
                      disabled={isLoading}
                      className="group relative p-2 text-gray-400 hover:text-blue-600 disabled:opacity-50"
                      title="Test connection"
                    >
                      <Wifi className={`h-4 w-4 ${isLoading ? 'animate-pulse' : ''}`} />
                      <span className="absolute right-0 top-8 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                        Test Connection
                      </span>
                    </button>
                  )}
                  <button
                    onClick={() => handleRefreshTools(server)}
                    disabled={isLoading}
                    className="group relative p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    title="Refresh tools"
                  >
                    <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                    <span className="absolute right-0 top-8 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                      Refresh/Discover Tools
                    </span>
                  </button>
                  <button
                    onClick={() => handleDeleteServer(server)}
                    className="group relative p-2 text-gray-400 hover:text-red-600"
                    title="Delete server"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="absolute right-0 top-8 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                      Delete Server
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Expanded Tools List */}
            {isExpanded && serverTools.length > 0 && (
              <div className="bg-gray-50">
                {/* Toggle All Tools Buttons */}
                <div className="px-6 py-2 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-xs text-gray-500">Toggle all tools:</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const toolIds = serverTools.map(tool => tool.id);
                        if (onBatchToolToggle) {
                          onBatchToolToggle(toolIds, true);
                        } else {
                          // Fallback to individual toggles
                          serverTools.forEach(tool => {
                            if (!selectedTools.includes(tool.id)) {
                              onToolToggle(tool.id, true);
                            }
                          });
                        }
                      }}
                      className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                    >
                      Enable All
                    </button>
                    <button
                      onClick={() => {
                        const toolIds = serverTools.map(tool => tool.id);
                        if (onBatchToolToggle) {
                          onBatchToolToggle(toolIds, false);
                        } else {
                          // Fallback to individual toggles
                          serverTools.forEach(tool => {
                            if (selectedTools.includes(tool.id)) {
                              onToolToggle(tool.id, false);
                            }
                          });
                        }
                      }}
                      className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      Disable All
                    </button>
                  </div>
                </div>
                
                <div className="px-6 py-2 space-y-2">
                  {serverTools.map((tool) => {
                    const isEnabled = selectedTools.includes(tool.id);

                    return (
                      <div
                        key={tool.id}
                        className="flex items-center justify-between py-2"
                      >
                      <div className="flex-1">
                        <h5 className="text-sm font-medium text-gray-700">{tool.name}</h5>
                        {tool.description && (
                          <p className="text-xs text-gray-500 mt-1">{tool.description}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => onToolToggle(tool.id, !isEnabled)}
                        className={`
                          relative inline-flex h-5 w-10 items-center rounded-full transition-colors
                          ${isEnabled ? 'bg-indigo-600' : 'bg-gray-200'}
                        `}
                      >
                        <span className="sr-only">Enable {tool.name}</span>
                        <span
                          className={`
                            inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform
                            ${isEnabled ? 'translate-x-5' : 'translate-x-1'}
                          `}
                        />
                      </button>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}